import React, { Children } from 'react';
import { useT } from '../i18n';
interface SlideTwoProps {
  onNext: () => void;
  onBack: () => void;
  onCTA: () => void;
  selectedOption: number | null;
}
interface OptionVariant {
  image: string;
  imageAlt: string;
  video?: string;
  headlineKey: string;
  bodyKey: string;
  pct: number;
}
function getVariants(lang: string): Record<number, OptionVariant> {
  return {
    0: {
      image: lang === 'da' ? 'https://images.unsplash.com/photo-1471506480208-91b3a4cc78be?auto=format&fit=crop&w=800&q=80' : 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?auto=format&fit=crop&w=800&q=80',
      imageAlt: lang === 'da' ? 'Children playing on a sandy beach' : 'Person cross-country skiing in Norwegian mountains',
      video: lang === 'no' ? 'https://cdn.magicpatterns.com/uploads/pkof6obycub4EfVMQQB7cK/Heliskiing In Iceland with Viking Heli Skiing Viking Heliskiing.mp4' : 'https://cdn.magicpatterns.com/uploads/m95jpoxuKfdbjSYt3VUfgx/Gaby and Alex on the Beach playing Sand and other Kids Toys.mp4',
      headlineKey: 'results.headline0',
      bodyKey: 'results.body0',
      pct: 42
    },
    1: {
      image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Modern dental clinic with orthodontic equipment',
      headlineKey: 'results.headline1',
      bodyKey: 'results.body1',
      pct: 23
    },
    2: {
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Orthodontist performing aligner bonding procedure',
      video: 'https://cdn.magicpatterns.com/uploads/2r5bNwXQz9DDxpP1cndNHv/videoplayback (10).mp4',
      headlineKey: 'results.headline2',
      bodyKey: 'results.body2',
      pct: 35
    }
  };
}
export function SlideTwo({
  onNext,
  onBack,
  onCTA,
  selectedOption
}: SlideTwoProps) {
  const {
    t,
    lang
  } = useT();
  const VARIANTS = getVariants(lang);
  const DEFAULT_VARIANT = VARIANTS[2];
  const variant = selectedOption !== null && VARIANTS[selectedOption] || DEFAULT_VARIANT;
  return <div className="flex flex-col h-full w-full justify-center" data-id="element-249">
      {/* Back button */}
      <button onClick={onBack} className="self-start mb-4 flex items-center text-solventum-secondary hover:text-white transition-colors duration-200 text-sm font-body group" aria-label="Go back" data-id="element-250">
        <svg className="w-4 h-4 mr-1.5 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-251">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" data-id="element-252" />
        </svg>
        {t('back')}
      </button>

      {/* Headline */}
      <div className="animate-fade-in-up opacity-0-init mb-4" style={{
      animationDelay: '0ms'
    }} data-id="element-253">
        <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight text-center" data-id="element-254">
          {t(variant.headlineKey)}
        </h2>
      </div>

      {/* Peer agreement — compact */}
      <div className="animate-fade-in-up opacity-0-init flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-4" style={{
      animationDelay: '200ms'
    }} data-id="element-255">
        <p className="text-solventum-primary text-xl font-headline font-extrabold flex-shrink-0" data-id="element-256">
          {variant.pct}%
        </p>
        <div className="flex-1" data-id="element-257">
          <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden mb-1" data-id="element-258">
            <div className="h-full rounded-full bg-solventum-primary transition-all duration-1000 ease-out" style={{
            width: `${variant.pct}%`
          }} data-id="element-259" />
          </div>
          <p className="text-white/70 text-xs font-body" data-id="element-260">
            {t('results.agree', {
            pct: variant.pct
          })}
          </p>
        </div>
      </div>

      {/* Body text — above image */}
      <div className="animate-fade-in-up opacity-0-init mb-4" style={{
      animationDelay: '400ms'
    }} data-id="element-261">
        <p className="text-lg text-white/90 font-body leading-relaxed text-center" data-id="element-262">
          {t(variant.bodyKey)}
        </p>
      </div>

      {/* Hero image */}
      <div className="animate-fade-in-up opacity-0-init w-full mb-5 rounded-2xl overflow-hidden relative" style={{
      animationDelay: '600ms'
    }} data-id="element-263">
        <div className="aspect-[16/9] w-full rounded-2xl overflow-hidden bg-white/5" data-id="element-264">
          {variant.video ? <video src={variant.video} autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-265" /> : <img src={variant.image} alt={variant.imageAlt} className="w-full h-full object-cover" data-id="element-266" />}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-solventum-bg to-transparent" data-id="element-267" />
      </div>

      {/* CTAs */}
      <div className="animate-fade-in-up opacity-0-init flex flex-col gap-3" style={{
      animationDelay: '800ms'
    }} data-id="element-268">
        <button onClick={onCTA} className="w-full bg-solventum-primary hover:bg-[#04c444] text-solventum-bg font-headline font-extrabold py-4 px-6 rounded-xl transition-colors duration-200 text-lg shadow-[0_0_15px_rgba(5,221,77,0.2)]" data-id="element-269">
          {t('results.cta')}
        </button>
        <button onClick={onNext} className="w-full bg-white/5 border border-white/15 hover:border-white/30 text-white font-headline font-bold py-3 px-6 rounded-xl transition-all duration-200 text-base" data-id="element-270">
          {t('results.next')}
        </button>
      </div>
    </div>;
}